import type { PixelProvider } from '@cod/config-schema';
import type { TrackingPixelInput } from '../api/client.js';

/**
 * The Tracking page's pure model: the draft shape, which providers can fire, and the draft → API
 * mapping.
 *
 * Separate from `TrackingPixels.tsx` because that module reaches the Nexo client at import time,
 * which needs a `window`. Keeping the rules here makes them testable in a node environment — and
 * they are the rules most worth testing, since getting them wrong yields a pixel the merchant
 * switched on that silently never fires.
 */

/** A pixel as edited on this page: the stored fields plus transient token-input state. The
 * access token is write-only — we never receive it, so we track whether the merchant typed a
 * new value (`tokenTouched`) and only send it then. */
export interface PixelDraft {
  id: string;
  provider: PixelProvider;
  pixel_id: string;
  label: string;
  enabled: boolean;
  server_enabled: boolean;
  has_token: boolean;
  failing: boolean;
  lastError: string | null;
  tokenInput: string;
  tokenTouched: boolean;
}

/**
 * Providers that support a server-side conversions API.
 *
 * 🚨 **This is now the list of providers that work at all.** Browser pixels are gone from both
 * storefront bundles (Phase 10 item 2): a NubeSDK app runs in a Web Worker and cannot inject
 * `fbevents.js`, `ttq` or `gtag`, so keeping the browser path on the legacy bundle would have meant
 * an Amazonas buyer firing a browser Purchase that a Patagonia buyer never fires. Everything now
 * goes out server-side — funnel events via `/public/events`, Purchase via the `order/created`
 * webhook.
 *
 * `google_ads` is absent and therefore **cannot fire**: server-side Google Ads conversions need the
 * Google Ads API (OAuth2 + developer token + Google approval), not a simple endpoint. The supported
 * path is to send the purchase to GA4 and link GA4 → Google Ads to import the conversion — merchant
 * configuration rather than code. The UI says so rather than leaving a dead toggle.
 *
 * For `ga4` the two credentials map onto the generic pixel row as:
 *   pixel id     → measurement id (`G-XXXXXXX`)
 *   access token → Measurement Protocol API secret
 */
export const SERVER_PROVIDERS: PixelProvider[] = ['meta', 'tiktok', 'ga4'];
/**
 * One pixel draft → the API shape.
 *
 * 🚨 The single toggle drives BOTH `enabled` and `server_enabled`, and it has to. The backend gates
 * on `server_enabled && enabled !== false` in two places (`funnelEvents.service.ts:122`,
 * `conversions.service.ts:46`), so leaving either one behind means a pixel the merchant switched on
 * that never fires — with nothing anywhere to say why.
 *
 * They stopped being distinct when the browser path was deleted (Phase 10 item 2): `enabled` meant
 * "fire in the browser", `server_enabled` meant "also fire server-side", and there is no browser
 * any more. A provider with no server path can never be on.
 */
export function pixelToInput(p: PixelDraft): TrackingPixelInput {
  const access_token = p.tokenTouched ? p.tokenInput.trim() || null : undefined;
  return {
    id: p.id,
    provider: p.provider,
    pixel_id: p.pixel_id.trim(),
    ...(p.provider === 'google_ads' && p.label.trim() ? { label: p.label.trim() } : {}),
    enabled: p.enabled,
    server_enabled: p.enabled && SERVER_PROVIDERS.includes(p.provider),
    ...(access_token !== undefined ? { access_token } : {}),
  };
}

/**
 * What a pixel's row says about itself, without the merchant having to open it.
 *
 * 🚨 The reason this exists: a pixel's real state was only visible once its card was EXPANDED. A
 * `failing` pixel — one whose last server-side send was rejected — showed a red alert inside the
 * card and nothing at all in the list, so a store could be silently losing every conversion while
 * the page looked fine. The same went for a pixel switched on with no access token, which cannot
 * fire either.
 *
 * Ordered by what the merchant should act on first, which is not the same as the order the fields
 * appear in:
 *
 *  1. `unsupported` — the provider has no server path at all, so nothing else about it matters.
 *     Calling a Google Ads pixel "Active" would be a straight lie; it never fires.
 *  2. `failing`     — the platform rejected our last send. The one state that loses live data.
 *  3. `inactive`    — switched off. An honest headline; whether it is also incomplete is moot.
 *  4. `incomplete`  — on, but missing something it needs: an id, or a token for a server provider.
 *  5. `active`      — configured and firing.
 */
export type PixelStatus = 'unsupported' | 'failing' | 'inactive' | 'incomplete' | 'active';

export function pixelStatus(p: PixelDraft): PixelStatus {
  if (!SERVER_PROVIDERS.includes(p.provider)) return 'unsupported';
  if (p.failing) return 'failing';
  if (!p.enabled) return 'inactive';
  // A token the merchant has typed but not yet saved still counts — the row should stop nagging
  // the moment they have supplied it, not only after the round-trip.
  const hasToken = p.has_token || p.tokenInput.trim() !== '';
  if (!p.pixel_id.trim() || !hasToken) return 'incomplete';
  return 'active';
}

/**
 * The platforms offered in a pixel's "Platform" dropdown.
 *
 * `google_ads` is not selectable: server-side Google Ads conversions need the Google Ads API
 * (OAuth2 + developer token + Google approval), which this app does not use, so the pixel can
 * never fire. Offering it was offering a dead choice that the card then spent a whole warning
 * Alert explaining.
 *
 * 🚨 It is NOT removed from `pixelProvider` in the schema, and must not be — a stored config still
 * naming it would fail validation and take the merchant's ENTIRE form config down with it. So a
 * pixel already saved as Google Ads keeps working: this returns the option for that pixel's own
 * dropdown, and only that one. Without it the `<select>` would hold a value it has no option for
 * and the browser would display some other platform's name on the merchant's pixel.
 *
 * Switching such a pixel to a real platform is one-way by design — the option vanishes from its
 * list the moment it is no longer the current value, which is exactly the intent.
 */
export const SELECTABLE_PROVIDERS: PixelProvider[] = ['meta', 'tiktok', 'ga4'];

export function providerOptionsFor(current: PixelProvider): PixelProvider[] {
  return SELECTABLE_PROVIDERS.includes(current)
    ? SELECTABLE_PROVIDERS
    : [...SELECTABLE_PROVIDERS, current];
}
